<script setup lang="ts">
/**
 * Full-screen mobile subscription (subscription type) detail view, opened from a
 * card in the subscriptions list. Same data as the desktop `SubscriptionTypeCard`
 * (name, validity, visits or "unlimited", price, covered service types); Edit
 * is in the navbar and Delete at the bottom, as on the other mobile detail
 * screens. Layout follows `docs/sketches/mobile/subscription-type-card.html`.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { deleteSubscriptionType, fetchSubscriptionType } from '@/api/subscriptionTypes'
import { useAddSubscriptionTypeFormStore } from '@/stores/add_subscription_type_form_store'
import { subscriptionTypesPollingInterval } from '@/config/api'
import { formatMoney, currencySymbol } from '@/utils/formatMoney'
import DeleteConfirmSheet from '@/components/main/DeleteConfirmSheet.vue'
import { useCoveredTypeLabels } from '@/composables/useCoveredTypeLabels'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'

const props = defineProps<{
  /** Subscription type id selected in the subscriptions list. */
  subscriptionTypeId: string
}>()

const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const { hasRole } = usePermissions()
const canEdit = computed(() => hasRole(ResourceAccessRole.SubscriptionTypesEdit))
const canDelete = computed(() => hasRole(ResourceAccessRole.SubscriptionTypesDelete))

const query = useQuery({
  queryKey: ['subscriptionType', computed(() => props.subscriptionTypeId)],
  queryFn: () => fetchSubscriptionType(props.subscriptionTypeId),
  refetchInterval: subscriptionTypesPollingInterval,
  refetchIntervalInBackground: false,
})

const subscriptionType = computed(() => query.data.value ?? null)

const { labelsFor } = useCoveredTypeLabels()
const coveredTypes = computed(() =>
  subscriptionType.value ? labelsFor(subscriptionType.value.business_service_type_ids) : [],
)

const priceLabel = computed<string | null>(() => {
  const price = subscriptionType.value?.price?.price
  if (price == null) return null
  return formatMoney({ amount: price, precision: 0, fractionDigits: 2 }).formatted
})

const addFormStore = useAddSubscriptionTypeFormStore()

function openEdit(): void {
  const type = subscriptionType.value
  if (!type) return
  // The edit form is a separate full-screen layer; this screen stays mounted
  // underneath, so closing the form returns here (no selection is cleared).
  addFormStore.openForEdit(type.id, {
    name: type.name,
    durationDays: type.duration,
    numberOfServices: type.number_of_services,
    price: type.price?.price ?? null,
    businessServiceTypeIds: [...type.business_service_type_ids],
  })
}

// ── Delete ────────────────────────────────────────────────────────────────────
const queryClient = useQueryClient()
const showDeleteConfirm = ref(false)

const deleteMutation = useMutation({
  mutationFn: () => deleteSubscriptionType(props.subscriptionTypeId),
  onSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['subscriptionTypes'] })
    showDeleteConfirm.value = false
    emit('close')
  },
})

const deleteDescription = computed<string>(() =>
  t('cards.subscriptionType.confirmDeleteDescription', {
    name: subscriptionType.value?.name ?? t('cards.subscriptionType.defaultName'),
  }),
)

function handleDelete(): void {
  if (!subscriptionType.value) return
  showDeleteConfirm.value = true
}
</script>

<template>
  <Teleport to="body">
    <div class="stm-screen font-tufte text-tufte-text">
      <!-- iOS navbar -->
      <nav class="stm-navbar">
        <button type="button" class="stm-nav-back" @click="emit('close')">
          <svg
            width="8"
            height="14"
            viewBox="0 0 8 14"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7 1L1 7L7 13" />
          </svg>
          {{ t('tabs.services.paneSubscriptions') }}
        </button>
        <span class="stm-nav-title">{{ t('cards.subscriptionType.navTitle') }}</span>
        <button
          v-if="canEdit"
          type="button"
          class="stm-nav-edit"
          :disabled="!subscriptionType"
          @click="openEdit"
        >
          {{ t('common.edit') }}
        </button>
      </nav>

      <div v-if="query.isPending.value && !subscriptionType" class="stm-state">
        {{ t('common.loadingShort') }}
      </div>
      <div v-else-if="query.isError.value && !subscriptionType" class="stm-state stm-state--error">
        {{ t('cards.subscriptionType.loadError') }}
      </div>

      <div v-else-if="subscriptionType" class="stm-scroll">
        <div class="stm-header">
          <div class="stm-name">{{ subscriptionType.name }}</div>
        </div>

        <div class="stm-stat-strip">
          <div class="stm-stat-cell">
            <div class="stm-stat-num">{{ subscriptionType.duration }}</div>
            <div class="stm-stat-cap">{{ t('cards.subscriptionType.validity') }}</div>
          </div>
          <div class="stm-stat-cell">
            <div
              class="stm-stat-num"
              :class="{ 'stm-stat-num--pos': subscriptionType.number_of_services === null }"
            >
              {{ subscriptionType.number_of_services ?? '∞' }}
            </div>
            <div class="stm-stat-cap">
              {{
                subscriptionType.number_of_services === null
                  ? t('cards.subscriptionType.unlimited')
                  : t('cards.subscriptionType.visits')
              }}
            </div>
          </div>
          <div class="stm-stat-cell">
            <div class="stm-stat-num stm-stat-num--pos">{{ priceLabel ?? '–' }}</div>
            <div class="stm-stat-cap">
              {{ t('cards.subscriptionType.price') }} {{ currencySymbol }}
            </div>
          </div>
        </div>

        <div class="stm-section-label">{{ t('cards.subscriptionType.coveredTypes') }}</div>
        <div class="stm-group">
          <div v-for="label in coveredTypes" :key="label" class="stm-type-row">
            {{ label }}
          </div>
          <div v-if="!coveredTypes.length" class="stm-type-empty">
            {{ t('cards.subscriptionType.noTypes') }}
          </div>
        </div>

        <div class="stm-fill" />

        <div v-if="canDelete" class="stm-danger-group">
          <button
            type="button"
            class="stm-danger-row"
            :disabled="deleteMutation.isPending.value"
            @click="handleDelete"
          >
            {{ deleteMutation.isPending.value ? t('common.deleting') : t('common.delete') }}
          </button>
        </div>

        <div class="stm-scroll-end" />
      </div>

      <DeleteConfirmSheet
        :open="showDeleteConfirm"
        :title="t('cards.subscriptionType.confirmDeleteTitle')"
        :description="deleteDescription"
        :pending="deleteMutation.isPending.value"
        @confirm="deleteMutation.mutate()"
        @cancel="showDeleteConfirm = false"
      />
    </div>
  </Teleport>
</template>

<style scoped>
.stm-screen {
  position: fixed;
  inset: 0;
  z-index: 10;
  background: #f4f3ea;
  display: flex;
  flex-direction: column;
}

.stm-navbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem 0.5rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.stm-nav-back {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  justify-content: flex-start;
}
.stm-nav-back:active {
  opacity: 0.6;
}

.stm-nav-title {
  font-family: var(--font-tufte-sans);
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  flex: 1;
  text-align: center;
}

.stm-nav-edit {
  font: inherit;
  font-size: 0.9rem;
  text-transform: uppercase;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  text-align: right;
}
.stm-nav-edit:disabled {
  color: var(--color-tufte-muted);
  cursor: default;
}
.stm-nav-edit:not(:disabled):active {
  opacity: 0.6;
}

.stm-state {
  padding: 1.5rem 1.25rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.9rem;
  color: var(--color-tufte-muted);
}
.stm-state--error {
  color: var(--color-money-negative);
}

.stm-scroll {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.stm-header {
  background: var(--color-tufte-bg);
  border-bottom: 1px solid var(--color-tufte-border);
  padding: 1.3rem 1.25rem 1.2rem;
}

.stm-name {
  font-size: 1.9rem;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.01em;
}

.stm-stat-strip {
  display: flex;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.stm-stat-cell {
  flex: 1;
  padding: 0.75rem 0.5rem;
  text-align: center;
  border-right: 1px solid var(--color-tufte-border);
}
.stm-stat-cell:last-child {
  border-right: none;
}

.stm-stat-num {
  font-size: 1.4rem;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.stm-stat-num--pos {
  color: var(--color-money-positive);
}

.stm-stat-cap {
  font-family: var(--font-tufte-sans);
  font-size: 0.64rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  margin-top: 0.3rem;
}

.stm-section-label {
  font-family: var(--font-tufte-sans);
  font-size: 0.7rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  padding: 0 1.25rem 0.4rem;
  margin-top: 1.2rem;
}

.stm-group {
  background: var(--color-tufte-bg);
  border-top: 1px solid var(--color-tufte-border);
  border-bottom: 1px solid var(--color-tufte-border);
}

.stm-type-row {
  padding: 0.7rem 1.25rem;
  border-bottom: 1px solid #eeeee8;
  font-size: 1.02rem;
}
.stm-type-row:last-child {
  border-bottom: none;
}

.stm-type-empty {
  padding: 0.75rem 1.25rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.85rem;
  color: var(--color-tufte-muted);
  font-style: italic;
}

.stm-fill {
  flex: 1;
}

.stm-danger-group {
  margin-top: 1.4rem;
}

.stm-danger-row {
  width: 100%;
  display: block;
  background: var(--color-tufte-bg);
  border: none;
  border-top: 1px solid var(--color-tufte-border);
  border-bottom: 1px solid var(--color-tufte-border);
  padding: 0.85rem 1.25rem;
  text-align: center;
  font: inherit;
  font-size: 0.95rem;
  text-transform: uppercase;
  color: var(--color-tufte-danger);
  cursor: pointer;
}
.stm-danger-row:active {
  background: color-mix(in srgb, var(--color-tufte-danger) 5%, var(--color-tufte-bg));
}
.stm-danger-row:disabled {
  opacity: 0.5;
  cursor: default;
}

.stm-scroll-end {
  height: 2.5rem;
}
</style>
